import { ConfettiIcon, SortingArrowDownIcon, SortingArrowUpIcon } from '@synergy/theme';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { ImportStatus } from '@synergy/models';
import { Localized, PoolButton, PoolTypography, useFilter } from '@synergy/components';
import { PaloAmount, PaloColumn, PaloListView } from '@synergy/components-palo';
import { makeStyles } from '@mui/styles';
import { useSearchParams } from 'react-router-dom';

import { ImportDetailTabs } from '../../models/import-detail-tabs';
import { ImportItem } from '../../models/import-item';
import { getImportStatusIcon } from '../../utils/get-import-status-icon';
import { useAppSelector } from '../../../../../store/use-appstate-selector';

const useStyles = makeStyles(() => ({
    statusIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    allDone: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
    },
}));

interface ImportDetailTableProps {
    selectedTab: ImportDetailTabs;
    selected: number[];
    onSelected: (selected: number[]) => void;
}

export const ImportDetailTable = (props: ImportDetailTableProps) => {
    const classes = useStyles();
    const { filter, reset: resetFilter } = useFilter();
    const [searchParams, setSearchParams] = useSearchParams();
    const { importJobItems, importDetails } = useAppSelector(state => state.import);
    const { selectedTab } = props;
    const isSelectable =
        selectedTab === ImportDetailTabs.Imported &&
        importDetails?.status &&
        [ImportStatus.ReadyToPublish, ImportStatus.Publishing, ImportStatus.Published].includes(importDetails.status);

    const columns: PaloColumn<ImportItem>[] = [
        {
            key: 'status',
            type: 'icon',
            head: <FormattedMessage id="app.table.column.status" />,
            cell: item => <div className={classes.statusIcon}>{getImportStatusIcon(item.changeState)}</div>,
        },
        {
            key: 'articleNumber',
            type: 'articleNumber',
            head: <FormattedMessage id="app.table.column.articleNumber" />,
            cell: item => item.number,
        },
        {
            key: 'articleName',
            type: 'name',
            head: <FormattedMessage id="app.table.column.articleName" />,
            cell: item => <Localized value={item.name} />,
        },
        {
            key: 'gtin',
            type: 'articleNumber',
            head: <FormattedMessage id="app.table.column.gtin" />,
            cell: item => item.globalTradeItemNumber,
        },
        {
            key: 'price',
            currency: 'EUR',
            type: 'price',
            head: <FormattedMessage id="app.table.column.price" />,
            cell: item => (
                <PaloAmount value={item.newPrice} currency={item.currency} base="EUR" />
            ),
        },
        {
            key: 'adaption',
            type: 'percent',
            head: <FormattedMessage id="app.table.column.adaption" />,
            cell: item =>
                item.priceChangeRatio ? (
                    <>
                        {item.priceChangeRatio < 0 ? (
                            <SortingArrowDownIcon htmlColor="#F94264" style={{ fontSize: 12 }} />
                        ) : (
                            <SortingArrowUpIcon htmlColor="#06BD0E" style={{ fontSize: 12 }} />
                        )}
                        <span style={{ color: item.priceChangeRatio < 0 ? '#F94264' : '#06BD0E', marginLeft: 8 }}>
                            <FormattedNumber
                                value={Math.abs(item.priceChangeRatio)}
                                style="percent"
                                minimumFractionDigits={2}
                                maximumFractionDigits={2}
                            />
                        </span>
                    </>
                ) : (
                    <>&ndash;</>
                ),
        },
    ];

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const pageIds = importJobItems.items.map(item => item.id);

    if (importJobItems.items.length > 0) {
        return (
            <PaloListView<ImportItem, number>
                embedded={true}
                columns={columns}
                items={importJobItems.items}
                getRowId={item => item.id}
                emptyLabel={<FormattedMessage id="app.common.emptyResult" />}
                selection={
                    isSelectable
                        ? {
                              selectedIds: props.selected,
                              // "All" means the page in front of the user, the
                              // way the old table counted it.
                              allSelected: pageIds.every(id => props.selected.includes(id)),
                              someSelected: pageIds.some(id => props.selected.includes(id)),
                              onToggle: id =>
                                  props.onSelected(
                                      props.selected.includes(id)
                                          ? props.selected.filter(selectedId => selectedId !== id)
                                          : [...props.selected, id]
                                  ),
                              onToggleAll: () =>
                                  props.onSelected(
                                      pageIds.every(id => props.selected.includes(id))
                                          ? props.selected.filter(id => !pageIds.includes(id))
                                          : [...new Set([...props.selected, ...pageIds])]
                                  ),
                          }
                        : undefined
                }
                pagination={{
                    count: importJobItems.total,
                    page: Number(searchParams.get('page') ?? 1),
                    limit: importJobItems.limit,
                    onPageChange: page => updateParams({ page: String(page) }),
                    onLimitChange: limit => updateParams({ limit: String(limit), page: '1' }),
                }}
            />
        );
    }

    if (Object.entries(filter).length > 0 && importJobItems.items.length === 0) {
        return (
            <>
                <PoolTypography id="app.common.emptyResult" />
                <br />
                <PoolButton styleType="firstLevel" onClick={resetFilter}>
                    <FormattedMessage id="app.filter.reset" />
                </PoolButton>{' '}
            </>
        );
    }

    return (
        <div className={classes.allDone}>
            {isSelectable ? (
                <>
                    <ConfettiIcon style={{ fontSize: 48, marginBottom: 24 }} />
                    <PoolTypography typography="h5" id="app.imports.detail.allDone" style={{ marginBottom: 8 }} />
                    <PoolTypography typography="body1" id="app.imports.detail.noArticlesLeft" />
                </>
            ) : (
                <PoolTypography typography="h5" id="app.imports.detail.noArticles" style={{ marginBottom: 8 }} />
            )}
        </div>
    );
};
